import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { onlyCompleteData } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, Injector, computed, inject, resource } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import {
  IpBgpSubscriptionDetailsGQL,
  IpBgpSubscriptionDetailsQuery,
  IpStaticSubscriptionDetailsGQL,
  IpStaticSubscriptionDetailsQuery,
  L2vpnSubscriptionDetailsGQL,
  L2vpnSubscriptionDetailsQuery,
  L3vpnSubscriptionDetailsGQL,
  L3vpnSubscriptionDetailsQuery,
  LightpathRedundantSubscriptionDetailsGQL,
  LightpathRedundantSubscriptionDetailsQuery,
  LightpathSubscriptionDetailsGQL,
  LightpathSubscriptionDetailsQuery,
} from "../../apis/networkdashboard/graphql/graphql";
import { StatsService } from "../../apis/networkdashboard/rest/services/stats.service";
import { LoaderComponent } from "../../components/common/loader/loader";
import { MonthNamePipe } from "../../helpers/datehelper/monthname";
import { GuidPipe } from "../../helpers/guidpipe/guidpipe";
import { SafePipe } from "../../helpers/safepipe/safepipe";
import { SlsEventDescriptionPipe } from "../../helpers/self-service/pipes/eventdescription";
import { ServiceSpeedPipe } from "../../helpers/service-speed-pipe/service-speed-pipe";
import { SubscriptionDatePipe } from "../../helpers/subscription-datepipe/subscription-datepipe";
import { AuthService } from "../../services/authservice";
import { AvailabilityExportFooterComponent } from "./components/availability-export-footer/availability-export-footer";
import { AvailabilityExportHeader } from "./components/availability-export-header/availability-export-header";

@Component({
  selector: "page-availability-export",
  templateUrl: "availability-export.html",
  styleUrls: ["availability-export.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    AvailabilityExportFooterComponent,
    AvailabilityExportHeader,
    DatePipe,
    GuidPipe,
    LoaderComponent,
    MonthNamePipe,
    SafePipe,
    ServiceSpeedPipe,
    SlsEventDescriptionPipe,
    SubscriptionDatePipe,
    TranslatePipe,
  ],
})
export class AvailabilityExportPage {
  private injector = inject(Injector);
  private route = inject(ActivatedRoute);
  private statsService = inject(StatsService);
  private auth = inject(AuthService);

  private subscriptionIdParam = this.route.snapshot.paramMap.get("id") ?? "";
  protected productType = this.route.snapshot.paramMap.get("type") ?? "";
  private productTag = this.route.snapshot.paramMap.get("tag");

  protected subscriptionResource = resource({
    params: () => ({
      subscriptionId: this.subscriptionIdParam,
      accessCustomerId: this.auth.viewingCustomer().customerId,
    }),
    loader: ({ params: { subscriptionId, accessCustomerId } }): Promise<any> => {
      const [gql, extractor] = this.getQuery(this.productTag ?? "");
      const query = this.injector.get<any>(gql);
      return firstValueFrom(
        query.watch({ variables: { subscriptionId, accessCustomerId } }).valueChanges.pipe(onlyCompleteData()),
      ).then((result: any) => extractor(result.data));
    },
  });
  protected subscription = this.subscriptionResource.value;
  protected subscriptionId = computed(() => this.subscription()?.subscriptionId);
  protected description = computed(() => this.subscription()?.description);
  protected customerDescription = computed(() => this.subscription()?.customerDescription);
  protected customerDescriptionExists = computed(() => this.customerDescription() !== this.description());

  protected slsReport = resource({
    params: () => this.subscriptionIdParam,
    loader: ({ params: subscriptionId }) =>
      this.statsService.getSubscriptionSlsYearReport({ subscription_id: subscriptionId }).then((sls) => ({
        yearAvailability: sls.yearAvailability,
        months: sls.months.map((m) => ({ ...m, date: new Date(m.firstDayOfTheMonth) })),
      })),
  });

  private getQuery(productTag: string): [any, any] {
    switch (productTag) {
      case "IPBGP":
        return [IpBgpSubscriptionDetailsGQL, (data: IpBgpSubscriptionDetailsQuery) => data.ipBgpDetails];
      case "IPS":
        return [IpStaticSubscriptionDetailsGQL, (data: IpStaticSubscriptionDetailsQuery) => data.ipStaticDetails];
      case "LP":
        return [LightpathSubscriptionDetailsGQL, (data: LightpathSubscriptionDetailsQuery) => data.lpDetails];
      case "LR":
        return [
          LightpathRedundantSubscriptionDetailsGQL,
          (data: LightpathRedundantSubscriptionDetailsQuery) => data.lrDetails,
        ];
      case "L3VPN":
        return [L3vpnSubscriptionDetailsGQL, (data: L3vpnSubscriptionDetailsQuery) => data.l3vpnDetails];
      case "L2VPN":
        return [L2vpnSubscriptionDetailsGQL, (data: L2vpnSubscriptionDetailsQuery) => data.l2vpnDetails];
      default:
        throw new Error(`Unsupported product tag: ${productTag}`);
    }
  }

  protected url = computed(
    () => `${window.location.hostname}/diensten/${this.productType}/${this.productTag}/${this.subscriptionId()}`,
  );
}
